import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'gd-jump-auto-retry';
const RETRY_DELAY_MS = 2000;

function loadPreference(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch (error) {
    console.warn('Could not load Jump auto-retry preference:', error);
    return false;
  }
}

export function useJumpAutoRetry(ready: boolean, onRetry: () => void) {
  const [enabled, setEnabled] = useState(loadPreference);
  const [storageError, setStorageError] = useState(false);
  const retryRef = useRef(onRetry);
  retryRef.current = onRetry;

  useEffect(() => {
    if (!enabled || !ready) {
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      clearTimeout(timer);
      if (!document.hidden) {
        timer = setTimeout(() => retryRef.current(), RETRY_DELAY_MS);
      }
    };
    schedule();
    document.addEventListener('visibilitychange', schedule);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', schedule);
    };
  }, [enabled, ready]);

  return (
    <div className="jump-auto-retry">
      <label>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => {
            const checked = event.target.checked;
            setEnabled(checked);
            try {
              localStorage.setItem(STORAGE_KEY, checked ? '1' : '0');
              setStorageError(false);
            } catch (error) {
              console.warn('Could not save Jump auto-retry preference:', error);
              setStorageError(true);
            }
          }}
        />{' '}
        Auto-retry when I crash
      </label>
      {enabled && ready && <p className="muted" role="status">Retrying in 2 seconds — untick to stop.</p>}
      {storageError && <p className="muted" role="status">This choice could not be saved on this device.</p>}
    </div>
  );
}
